import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Eye, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { SlideOver } from '@/components/SlideOver.js';
import {
  createCareGuideTemplate,
  deleteCareGuideTemplate,
  listCareGuideTemplates,
  previewCareGuideTemplate,
  updateCareGuideTemplateById,
  type CareGuideScopeType,
  type CareGuideSendChannel,
  type CareGuideTemplate,
  type CareGuideTip,
} from '@/lib/api/care-guides.js';
import { listDeviceMakes } from '@/lib/api/devices.js';
import { prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';
import { isManagerRole } from '@/lib/permissions.js';

const DEVICE_CATEGORIES = ['phone', 'tablet', 'laptop', 'desktop', 'console', 'wearable', 'other'];

const KNOWN_REPAIR_TYPES = [
  'screen_replacement',
  'battery',
  'water_damage',
  'charging_port',
  'camera',
  'data_recovery',
  'console',
  'laptop',
  'general',
];

type EditorState = {
  repairType: string;
  title: string;
  emailSubject: string;
  intro: string;
  warrantyReminder: string;
  contactInfoShown: boolean;
  active: boolean;
  tips: CareGuideTip[];
  sendChannel: CareGuideSendChannel;
  sortOrder: number;
  scopeType: CareGuideScopeType;
  scopeCategories: string[];
  scopeMakeIds: string[];
};

function fromTemplate(t: CareGuideTemplate): EditorState {
  return {
    repairType: t.repairType,
    title: t.title,
    emailSubject: t.emailSubject ?? t.title,
    intro: t.intro ?? '',
    warrantyReminder: t.warrantyReminder ?? '',
    contactInfoShown: t.contactInfoShown,
    active: t.active,
    tips: t.tips ?? [],
    sendChannel: t.sendChannel ?? 'both',
    sortOrder: t.sortOrder ?? 999,
    scopeType: t.scopeType ?? 'all_devices',
    scopeCategories: t.scopeCategories ?? [],
    scopeMakeIds: t.scopeMakeIds ?? [],
  };
}

function emptyEditor(): EditorState {
  return {
    repairType: 'custom_guide',
    title: '',
    emailSubject: '',
    intro: '',
    warrantyReminder: '',
    contactInfoShown: true,
    active: true,
    tips: [{ heading: 'Tip', content: '' }],
    sendChannel: 'both',
    sortOrder: 999,
    scopeType: 'all_devices',
    scopeCategories: [],
    scopeMakeIds: [],
  };
}

function toPayload(e: EditorState) {
  return {
    repairType: e.repairType.trim().toLowerCase().replace(/\s+/g, '_'),
    title: e.title.trim(),
    emailSubject: e.emailSubject.trim() || e.title.trim(),
    intro: e.intro.trim() || null,
    tips: e.tips.filter((t) => t.heading.trim()),
    warrantyReminder: e.warrantyReminder.trim() || null,
    contactInfoShown: e.contactInfoShown,
    sendChannel: e.sendChannel,
    sortOrder: e.sortOrder,
    scopeType: e.scopeType,
    scopeCategories: e.scopeType === 'categories' ? e.scopeCategories : [],
    scopeMakeIds: e.scopeType === 'makes' ? e.scopeMakeIds : [],
    active: e.active,
  };
}

function TemplateEditor({
  template,
  isCreate,
  canEdit,
  onClose,
}: {
  template: CareGuideTemplate | null;
  isCreate: boolean;
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<EditorState>(
    () => (template ? fromTemplate(template) : emptyEditor()),
  );
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [unresolved, setUnresolved] = useState<string[]>([]);
  const [sampleMode, setSampleMode] = useState<string>('');

  const makesQ = useQuery({
    queryKey: ['device-makes'],
    queryFn: () => listDeviceMakes(),
    enabled: canEdit,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = toPayload(form);
      if (isCreate || !template) return createCareGuideTemplate(body);
      return updateCareGuideTemplateById(template.id, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-guide-templates'] });
      toastSuccess(isCreate ? 'Care guide created' : 'Care guide saved');
      onClose();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const previewMut = useMutation({
    mutationFn: () =>
      previewCareGuideTemplate({
        draft: toPayload(form),
        ...(template ? { templateId: template.id } : {}),
      }),
    onSuccess: (g) => {
      setPreviewHtml(g.html);
      setUnresolved(g.unresolvedTokens ?? []);
      setSampleMode(g.sampleMode === 'ticket' ? 'Using ticket sample' : 'Using sample customer data');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function patch(p: Partial<EditorState>) {
    setForm((f) => ({ ...f, ...p }));
  }

  return (
    <div className="space-y-4">
      {!canEdit ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          View only — manager role required to edit care guides.
        </p>
      ) : null}

      <label className="block text-sm">
        <span className="font-medium">Name (title)</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={form.title}
          onChange={(e) => patch({ title: e.target.value })}
          disabled={!canEdit}
          required
        />
      </label>

      <label className="block text-sm">
        <span className="font-medium">Applies-to key (repair type)</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono text-sm"
          value={form.repairType}
          onChange={(e) => patch({ repairType: e.target.value })}
          disabled={!canEdit || !isCreate}
          list="care-guide-repair-types"
          placeholder="screen_replacement"
        />
        <datalist id="care-guide-repair-types">
          {KNOWN_REPAIR_TYPES.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
        <p className="mt-1 text-xs text-slate-500">
          Used to match repairs (e.g. battery, screen_replacement). Custom keys allowed on create.
        </p>
      </label>

      <label className="block text-sm">
        <span className="font-medium">Email subject</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={form.emailSubject}
          onChange={(e) => patch({ emailSubject: e.target.value })}
          disabled={!canEdit}
          placeholder="Care guide from {store_name}"
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm">
          <span className="font-medium">Send channel</span>
          <select
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={form.sendChannel}
            onChange={(e) => patch({ sendChannel: e.target.value as CareGuideSendChannel })}
            disabled={!canEdit}
          >
            <option value="both">Email &amp; SMS</option>
            <option value="email">Email only</option>
            <option value="sms">SMS only</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium">Sort order</span>
          <input
            type="number"
            min={0}
            max={9999}
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={form.sortOrder}
            onChange={(e) => patch({ sortOrder: Number(e.target.value) || 0 })}
            disabled={!canEdit}
          />
        </label>
      </div>

      <label className="block text-sm">
        <span className="font-medium">Device scope</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={form.scopeType}
          onChange={(e) => patch({ scopeType: e.target.value as CareGuideScopeType })}
          disabled={!canEdit}
        >
          <option value="all_devices">All devices</option>
          <option value="categories">Device categories</option>
          <option value="makes">Device makes</option>
        </select>
      </label>

      {form.scopeType === 'categories' ? (
        <div className="flex flex-wrap gap-2">
          {DEVICE_CATEGORIES.map((c) => {
            const on = form.scopeCategories.includes(c);
            return (
              <button
                key={c}
                type="button"
                disabled={!canEdit}
                onClick={() =>
                  patch({
                    scopeCategories: on
                      ? form.scopeCategories.filter((x) => x !== c)
                      : [...form.scopeCategories, c],
                  })
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-semibold',
                  on ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 text-slate-600',
                )}
              >
                {c}
              </button>
            );
          })}
        </div>
      ) : null}

      {form.scopeType === 'makes' ? (
        <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2">
          {(makesQ.data ?? []).map((m) => {
            const on = form.scopeMakeIds.includes(m.id);
            return (
              <label key={m.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!canEdit}
                  onChange={() =>
                    patch({
                      scopeMakeIds: on
                        ? form.scopeMakeIds.filter((id) => id !== m.id)
                        : [...form.scopeMakeIds, m.id],
                    })
                  }
                />
                {m.name}
              </label>
            );
          })}
        </div>
      ) : null}

      <label className="block text-sm">
        <span className="font-medium">Intro (body)</span>
        <textarea
          className="mt-1 min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={form.intro}
          onChange={(e) => patch({ intro: e.target.value })}
          disabled={!canEdit}
          placeholder="Thank you for choosing {store_name}!"
        />
        <p className="mt-1 text-xs text-slate-500">
          Merge fields: {'{store_name}'}, {'{customer_name}'}, {'{device}'}, {'{repair_type}'},{' '}
          {'{warranty_days}'}, {'{store_phone}'}
        </p>
      </label>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-slate-700">Tips (body sections)</span>
          {canEdit ? (
            <button
              type="button"
              onClick={() => patch({ tips: [...form.tips, { heading: 'New tip', content: '' }] })}
              className="text-xs font-semibold text-primary"
            >
              + Add tip
            </button>
          ) : null}
        </div>
        {form.tips.map((tip, index) => (
          <div key={index} className="rounded-xl border border-slate-200 p-3">
            <input
              className="mb-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm font-semibold"
              value={tip.heading}
              disabled={!canEdit}
              onChange={(e) =>
                patch({
                  tips: form.tips.map((row, i) =>
                    i === index ? { ...row, heading: e.target.value } : row,
                  ),
                })
              }
            />
            <textarea
              className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              rows={3}
              value={tip.content}
              disabled={!canEdit}
              onChange={(e) =>
                patch({
                  tips: form.tips.map((row, i) =>
                    i === index ? { ...row, content: e.target.value } : row,
                  ),
                })
              }
            />
            {canEdit ? (
              <button
                type="button"
                className="mt-2 text-xs font-semibold text-red-600"
                onClick={() => patch({ tips: form.tips.filter((_, i) => i !== index) })}
              >
                Remove
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <label className="block text-sm">
        <span className="font-medium">Warranty reminder</span>
        <textarea
          className="mt-1 min-h-16 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={form.warrantyReminder}
          onChange={(e) => patch({ warrantyReminder: e.target.value })}
          disabled={!canEdit}
        />
      </label>

      <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium">
        <span>Show store contact info</span>
        <input
          type="checkbox"
          checked={form.contactInfoShown}
          disabled={!canEdit}
          onChange={(e) => patch({ contactInfoShown: e.target.checked })}
        />
      </label>
      <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-sm font-medium">
        <span>Active (inactive guides are hidden from send)</span>
        <input
          type="checkbox"
          checked={form.active}
          disabled={!canEdit}
          onChange={(e) => patch({ active: e.target.checked })}
        />
      </label>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-slate-800">Merge-field preview</span>
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-3 py-1 text-xs font-semibold hover:bg-slate-50"
            onClick={() => previewMut.mutate()}
            disabled={previewMut.isPending}
          >
            {previewMut.isPending ? 'Rendering…' : 'Preview'}
          </button>
        </div>
        {sampleMode ? <p className="mb-2 text-xs text-slate-500">{sampleMode}</p> : null}
        {unresolved.length > 0 ? (
          <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-900">
            Unrecognized tokens: {unresolved.map((t) => `{${t}}`).join(', ')}
          </p>
        ) : null}
        {previewHtml ? (
          <div
            className="max-h-64 overflow-y-auto rounded-lg bg-white p-3 text-sm ring-1 ring-slate-200"
            // Preview HTML is built server-side from trusted merge of staff template content
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        ) : (
          <p className="text-xs text-slate-500">
            Preview resolves tokens against a sample customer/device so broken fields never reach a
            real send.
          </p>
        )}
      </div>

      {canEdit ? (
        <button
          type="button"
          disabled={saveMut.isPending || !form.title.trim()}
          className="w-full rounded-xl bg-primary py-2.5 text-sm font-bold text-white disabled:opacity-50"
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? 'Saving…' : isCreate ? 'Create care guide' : 'Save changes'}
        </button>
      ) : null}
    </div>
  );
}

export function CareGuidesSettingsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // Matches API requireRole('store_manager') — front desk can view, not write
  const canEdit = isManagerRole(user?.role ?? '');

  const [editTemplate, setEditTemplate] = useState<CareGuideTemplate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<CareGuideTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['care-guide-templates'],
    queryFn: () => listCareGuideTemplates({ active: 'all' }),
  });

  const templates = useMemo(
    () =>
      [...(templatesQuery.data ?? [])].sort(
        (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999) || a.repairType.localeCompare(b.repairType),
      ),
    [templatesQuery.data],
  );

  const deactivateMut = useMutation({
    mutationFn: (t: CareGuideTemplate) =>
      updateCareGuideTemplateById(t.id, { active: !t.active }),
    onSuccess: (_d, t) => {
      queryClient.invalidateQueries({ queryKey: ['care-guide-templates'] });
      toastSuccess(t.active ? 'Guide deactivated' : 'Guide reactivated');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const deleteMut = useMutation({
    mutationFn: (t: CareGuideTemplate) => deleteCareGuideTemplate(t.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-guide-templates'] });
      toastSuccess('Care guide deleted');
    },
    onError: (e) => {
      const parsed = parseApiError(e);
      if (parsed.code === 'HAS_SEND_HISTORY') {
        toastError(`${parsed.message} Use Deactivate instead.`);
      } else {
        toastError(parsed.message);
      }
    },
  });

  const previewOpenMut = useMutation({
    mutationFn: (t: CareGuideTemplate) =>
      previewCareGuideTemplate({ templateId: t.id, draft: fromTemplate(t) }),
    onSuccess: (g) => setPreviewHtml(g.html),
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-bold text-slate-950">Care Guide Templates</h2>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Post-repair guides sent at checkout or from a ticket. Deactivate to hide from the send
              picker; hard delete only works if the guide has never been sent.
            </p>
          </div>
          {canEdit ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-white"
            >
              <Plus className="h-4 w-4" />
              New guide
            </button>
          ) : null}
        </div>
      </div>

      {templatesQuery.isLoading ? (
        <p className="text-slate-500">Loading templates…</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {templates.map((template) => (
            <article
              key={template.id}
              className={cn(
                'rounded-2xl border bg-white p-5 shadow-sm',
                template.active ? 'border-slate-200' : 'border-slate-200 opacity-70',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    {prettyLabel(template.repairType)} · sort {template.sortOrder}
                  </p>
                  <h3 className="text-lg font-bold text-slate-950">{template.title}</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {template.tips.length} tips · {template.sendChannel} ·{' '}
                    {prettyLabel(template.scopeType)} ·{' '}
                    {template.active ? (
                      <span className="text-emerald-700">Active</span>
                    ) : (
                      <span className="text-slate-500">Inactive</span>
                    )}
                    {(template.sendCount ?? 0) > 0
                      ? ` · sent ${template.sendCount}×`
                      : ' · never sent'}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setEditTemplate(template)}
                  className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
                >
                  <Pencil className="h-4 w-4" />
                  {canEdit ? 'Edit' : 'View'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPreviewTemplate(template);
                    setPreviewHtml(null);
                    previewOpenMut.mutate(template);
                  }}
                  className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </button>
                {canEdit ? (
                  <>
                    <button
                      type="button"
                      onClick={() => deactivateMut.mutate(template)}
                      className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
                    >
                      <Power className="h-4 w-4" />
                      {template.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          !window.confirm(
                            (template.sendCount ?? 0) > 0
                              ? 'This guide may have send history. Delete only works if never sent — continue?'
                              : `Permanently delete “${template.title}”?`,
                          )
                        ) {
                          return;
                        }
                        deleteMut.mutate(template);
                      }}
                      className="inline-flex items-center gap-1 rounded-xl border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  </>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}

      <SlideOver
        title={createOpen ? 'New care guide' : editTemplate ? `Edit — ${editTemplate.title}` : 'Edit'}
        open={createOpen || Boolean(editTemplate)}
        onClose={() => {
          setCreateOpen(false);
          setEditTemplate(null);
        }}
        panelClassName="max-w-lg"
      >
        {createOpen || editTemplate ? (
          <TemplateEditor
            key={createOpen ? 'create' : editTemplate!.id}
            template={createOpen ? null : editTemplate}
            isCreate={createOpen}
            canEdit={canEdit}
            onClose={() => {
              setCreateOpen(false);
              setEditTemplate(null);
            }}
          />
        ) : null}
      </SlideOver>

      <SlideOver
        title={previewTemplate ? `Preview — ${previewTemplate.title}` : 'Preview'}
        open={Boolean(previewTemplate)}
        onClose={() => setPreviewTemplate(null)}
        panelClassName="max-w-lg"
      >
        {previewHtml ? (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        ) : (
          <p className="text-sm text-slate-500">Loading preview…</p>
        )}
      </SlideOver>
    </div>
  );
}
